import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiCalendar, FiInfo, FiCheckCircle } from 'react-icons/fi';
import { useAppContext } from '../../context/AppContext';

const LeaveApply = () => {
  const navigate = useNavigate();
  const { leave } = useAppContext();
  const { applyLeave, getMyBalance, loading } = leave;

  const [formData, setFormData] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    attachment: null,
    save_as_draft: false,
  });

  const [balance, setBalance] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadBalance();
  }, []);
  useEffect(() => {
    if (success) {
      const timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/leave/history');
            return 0;
          }
          return prev - 1;
        });
      }, 2000);

      return () => clearInterval(timer);
    }
  }, [success, navigate]);

  const loadBalance = async () => {
    try {
      const data = await getMyBalance();
      setBalance(data);
    } catch (err) {
    }
  };


  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, attachment: files[0] || null }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end < start) {
        newErrors.end_date = 'End date must be after start date';
      }
    }
    if (!formData.reason || formData.reason.trim().length < 10) {
      newErrors.reason = 'Please provide a detailed reason (minimum 10 characters)';
    }
    if (formData.reason && formData.reason.length > 500) {
      newErrors.reason = 'Reason must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // src/components/Leave/LeaveApply.jsx - Fix the calculateDays function

  // ✅ CORRECTED: Calculate days between two dates
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    if (end < start) return 0;

    // ✅ Calculate difference in days (inclusive)
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    return diffDays;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSuccess(null);
    setErrors({});

    try {
      const data = new FormData();
      data.append('leave_type', formData.leave_type);
      data.append('start_date', formData.start_date);
      data.append('end_date', formData.end_date);
      data.append('reason', formData.reason.trim());
      data.append('save_as_draft', formData.save_as_draft);
      if (formData.attachment) {
        data.append('attachment', formData.attachment);
      }

      const result = await applyLeave(data);

      // ✅ Set success message with redirect countdown
      const message = formData.save_as_draft
        ? 'Leave saved as draft successfully!'
        : 'Leave applied successfully! HR has been notified.';

      setSuccess({
        message: message,
        id: result.id,
        isDraft: formData.save_as_draft,
      });
      setRedirectCountdown(3);

      // ✅ If draft, stay on page with option to go to history
      if (formData.save_as_draft) {
        // Don't auto redirect for drafts
        setRedirectCountdown(0);
      }

      // ✅ Reset form after success (for non-drafts)
      if (!formData.save_as_draft) {
        setFormData({
          leave_type: 'casual',
          start_date: '',
          end_date: '',
          reason: '',
          attachment: null,
          save_as_draft: false,
        });
        loadBalance();
      }

    } catch (err) {
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'object') {
          setErrors(errorData);
        } else {
          setErrors({ general: errorData.error || 'Failed to apply for leave' });
        }
      } else {
        setErrors({ general: err.message || 'An unexpected error occurred' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Handle manual redirect to history
  const goToHistory = () => {
    navigate('/leave/history');
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/leave/history')}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <FiArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Apply for Leave</h1>
          <p className="text-gray-500">Fill in the details below to request leave</p>
        </div>
      </div>

      {/* Balance Display */}
      {balance && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 mb-6 border border-indigo-100">
          <div className="flex items-center gap-2 mb-2">
            <FiInfo className="text-indigo-500" />
            <h3 className="text-sm font-semibold text-gray-700">Your Leave Balance</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Casual</p>
              <p className="text-lg font-bold text-gray-800">
                {balance.casual_remaining || 0} <span className="text-sm font-normal text-gray-400">/ {balance.casual_total || 0}</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Sick</p>
              <p className="text-lg font-bold text-gray-800">
                {balance.sick_remaining || 0} <span className="text-sm font-normal text-gray-400">/ {balance.sick_total || 0}</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Unpaid</p>
              <p className="text-lg font-bold text-gray-800">{balance.unpaid_days || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Year</p>
              <p className="text-lg font-bold text-gray-800">{balance.year}</p>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Success Message with Redirect */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <FiCheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="font-medium text-green-700">{success.message}</p>
              {success.id && (
                <p className="text-sm text-green-600 mt-1">Request ID: #{success.id}</p>
              )}
              {!success.isDraft && redirectCountdown > 0 && (
                <p className="text-sm text-green-600 mt-2">
                  Redirecting to leave history in {redirectCountdown} second{redirectCountdown > 1 ? 's' : ''}...
                </p>
              )}
              <div className="flex gap-3 mt-3">
                {success.isDraft ? (
                  <button
                    onClick={goToHistory}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm"
                  >
                    View My Drafts
                  </button>
                ) : (
                  <button
                    onClick={goToHistory}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm"
                  >
                    Go to Leave History
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errors.general}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Leave Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Leave Type *
          </label>
          <select
            name="leave_type"
            value={formData.leave_type}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="casual">Casual Leave</option>
            <option value="sick">Sick Leave</option>
            <option value="unpaid">Unpaid Leave</option>
          </select>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date *
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              min={today}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.start_date ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.start_date && (
              <p className="text-sm text-red-500 mt-1">{errors.start_date}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date *
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              min={formData.start_date || today}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.end_date ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.end_date && (
              <p className="text-sm text-red-500 mt-1">{errors.end_date}</p>
            )}
          </div>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason *
          </label>
          <textarea
            name="reason"
            rows="4"
            placeholder="Please describe the reason for leave..."
            value={formData.reason}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.reason ? 'border-red-500' : 'border-gray-300'}`}
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">
              {formData.reason.length}/500 characters
            </span>
            {errors.reason && (
              <span className="text-sm text-red-500">{errors.reason}</span>
            )}
          </div>
        </div>

        {/* Attachment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attachment (Optional)
          </label>
          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-500 transition">
                <FiUpload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                <p className="text-sm text-gray-500">
                  {formData.attachment ? formData.attachment.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-400">
                  Max size: 5MB. Supported: PDF, JPG, PNG, DOC, DOCX
                </p>
              </div>
              <input
                type="file"
                name="attachment"
                onChange={handleChange}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                className="hidden"
              />
            </label>
            {formData.attachment && (
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, attachment: null }))}
                className="text-red-500 hover:text-red-700 text-sm font-medium"
              >
                Remove
              </button>
            )}
          </div>
          {errors.attachment && (
            <p className="text-sm text-red-500 mt-1">{errors.attachment}</p>
          )}
        </div>

        {/* Draft Option */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            name="save_as_draft"
            id="save_as_draft"
            checked={formData.save_as_draft}
            onChange={handleChange}
            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="save_as_draft" className="text-sm text-gray-700 cursor-pointer">
            Save as draft (submit later)
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/leave/history')}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || loading}
            className="flex-1 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Submitting...
              </span>
            ) : (
              formData.save_as_draft ? 'Save Draft' : 'Apply Leave'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeaveApply;